/**
 * BookingControls.js contains methods directly related to the stepper component
 * and how it navigates through step sequences
 * @see Stepper.vue
 */
export default {
    data() {
        return {
            stepNumber: 1,
            stepHistory: [],
        };
    },
    methods: {
        getSortIndex(step) {
            if (!this.stepSequence.length) return;
            return this.stepSequence.map((e) => e.name).indexOf(step);
        },
        /**
         * proceed in step sequence
         * wait for next tick in case component has to remount. avoids animation stutter
         */
        nextStep() {
            let firstNull = Object.values(this.selectableOptions).find(
                (e) => this.form[e.name] == null || e.revisit
            );

            if (!firstNull) return console.log("No null steps found");
            this.$nextTick(() => {
                this.stepNumber = firstNull.step;
            });
        },
        /**
         * clears form after step parameter in booking sequence
         * @param {object} step
         * @returns {Promise<void>}
         */
        prepareJumpToStep(step) {
            if (!step) return;

            if (step.noIndex) {
                step = this.$_controls_rebuildStepsFromHistory(step);
            }

            let clearSteps = this.stepSequence.filter((e) => {
                return e.step >= step.step;
            });

            clearSteps.forEach((e) => e.clear());
        },
        /**
         * noIndex will be set in $_options_
         * only works if booking [component].selection.base is false
         * steps must be rebuilt if a no index step is cleared
         * otherwise whole form could potentially be cleared, depending on the repositioning of the sequence after noIndex step is cleared
         * allows a step that would be a base selection to remain selectable
         */
        $_controls_rebuildStepsFromHistory(step) {
            let saveHistory = this.stepHistory;
            this.stepHistory = [];
            this.stepSequence = [];

            saveHistory.forEach((e) => {
                let findInSequence = e.addToSequence.find(
                    (sequence) => sequence.name == step.name
                );

                if (findInSequence) findInSequence.noIndex = false;

                this.$_controls_setStepData(
                    e.formData,
                    e.componentData,
                    e.addToSequence,
                    e.stepNumber,
                    false
                );
            });
            return this.stepSequence.find((e) => e.name == step.name);
        },
        /**
         * Go back in step sequence and clear related form data.
         * @see previousStep
         * previousStep.backstep() will execute component specific backstep
         * previousStep.persistent will prevent form from clearing
         */
        backStep() {
            this.previousStep?.clear();
            this.currentStep?.clear();
            this.stepNumber--;
            if (this.stepNumber < 1) this.$emit("reset");
            this.$_controls_clearStepData(this.currentStep?.step);
        },
        $_controls_blurInput() {
            document.activeElement.blur();
        },
        /**
         * set stepNumber to first null form item
         * @param {object} step
         * @see prepareJumpToStep
         * @returns void
         */
        jumpToStep(step) {
            this.prepareJumpToStep(step);
            this.nextStep();
            this.$_controls_clearStepData(step.step);
        },
        /**
         * @since v1.4.08
         * Sets related form and component data for each step
         * Saves data state and related method in stepHistory
         * Build steps from sequence
         * @param {object} formData any data set on form object
         * @param {object} componentData any data set on components, ex: this.assessment in @see CreateLesson.vue
         * @param {Number} stepNumber only pass from parent if coming from $_source, essentially acts as index 0, but stepNumber is base 1
         * @see createLesson.vue or other parent component
         * @see BookingStepComponents.js
         * @param {array} addToSequence
         * @return {void}
         */
        $_controls_setStepData(
            formData = {},
            componentData = {},
            addToSequence = [],
            stepNumber = this.stepNumber,
            setFormData = true
        ) {
            // Save data to form
            if (setFormData) {
                this.$_controls_setFormData(formData, componentData);
            }

            // merge addToSequence and load booking components
            let getSequence = this.$_controls_getSequence(addToSequence);

            let addToBase = this.$_controls_buildBaseSelection(
                formData,
                getSequence
            );

            let baseHistory = [
                ...this.stepHistory.flatMap((history) => history.addToBase),
                ...addToBase,
            ];

            this.stepSequence = [
                ...this.stepSequence,
                ...getSequence.filter((e) => !e.hide),
            ];

            let log = false;

            if (log) {
                console.log(
                    //this.stepHistory.flatMap(e => e.addToBase),
                    this.stepHistory.flatMap((e) =>
                        e.addToSequence.map((e) => e.name)
                    )
                    //this.stepHistory.flatMap(e => Object.keys(e.formData))
                );
            }

            let selectable = this.$_controls_setStepNumber(baseHistory);

            // set base and index values for stepSequence
            this.stepSequence.forEach((e) => {
                let getIndex = selectable.find(
                    (step) => step.name == e.name
                )?.step;
                e.base = baseHistory.includes(e.name);
                e.step = getIndex >= 0 ? getIndex + 1 : null;
            });

            this.$_controls_buildStepHistory(
                stepNumber,
                formData,
                componentData,
                addToSequence,
                addToBase
            );
        },
        $_controls_setFormData(formData = {}, componentData = {}) {
            Object.keys(formData).forEach((e) => {
                this.form[e] = formData[e];
            });

            Object.keys(componentData).forEach((e) => {
                this.$data[e] = componentData[e];
            });
        },
        $_controls_getSequence(addToSequence) {
            let getSequence = [];
            // Call new components and merge with $_options_
            addToSequence.forEach((step) => {
                //if (step.autoFill) this.form[step.name] = step.autoFill();
                //if (step.callback) step.callback();

                // create sequence array and overload component properties
                getSequence.push({
                    ...this.bookingComponents[step.name](),
                    ...step,
                });
            });

            return getSequence;
        },
        /**
         * Base selection is determined by any formData found in sequence
         * This will be visually represented, not entire addToBase object
         * Some formData will be hidden if not in getSequence, which is intentional
         * Don't want to always display every value to the user - stepper real estate is limited!
         * @param {object} formData data being set by stepHistory
         * @param {object} getSequence steps created by stepHistory
         * @returns {array}
         */
        $_controls_buildBaseSelection(formData, getSequence) {
            let addToBase = Object.keys(formData).filter((e) => {
                let findInSequence = getSequence.find(
                    (sequence) => sequence.name == e
                );

                // Prevent overriding steps from being added to base if they weren't originally
                let findInHistory = this.stepSequence.find((history) => {
                    return history.name == e;
                });

                if (findInHistory?.selection?.base == false) return;
                if (findInSequence?.selection?.base == false) return;

                return this.form[e] !== null;
            });

            return addToBase;
        },
        $_controls_setStepNumber(baseHistory) {
            let selectable = this.stepSequence.filter((e) => {
                //if (e.value()) return;
                //if (e.selection?.base == false && e.value()) return;
                if (e.noIndex) return;
                return !baseHistory.includes(e.name);
            });

            // Set step #
            selectable.forEach((e, idx) => {
                e.step = idx;
                //if (e.callback) e.callback();
            });

            return selectable;
        },
        $_controls_buildStepHistory(
            stepNumber,
            formData,
            componentData,
            addToSequence,
            addToBase
        ) {
            let log = false;

            if (log) {
                this.stepSequence.forEach((e) => {
                    console.table({
                        [e.name]: {
                            step: e.step,
                            base: e.base,
                            hide: e.hide,
                            value: !!e.value(),
                        },
                    });
                });
            }

            this.stepHistory.push({
                stepNumber: stepNumber,
                formData: formData,
                componentData: componentData,
                addToSequence: addToSequence,
                addToBase: addToBase,
            });
        },
        /**
         * runs after backStep() or jumpToStep()
         */
        $_controls_clearStepData(currentStep) {
            if (!currentStep) return;

            const reverseHistory = [...this.stepHistory].reverse();

            let log = false;

            // Loop handles if multiple history items are crossed by jumpToStep()
            reverseHistory.forEach((getStep, i) => {
                if (getStep.stepNumber < currentStep) return;

                if (log) {
                    console.table(getStep.addToSequence.map((e) => e.name));
                }

                // filter out if step sequence includes any items added by this history item
                this.stepSequence = this.stepSequence.filter((sequence) => {
                    if (log) console.log(sequence.name);
                    return !getStep.addToSequence
                        .map((e) => e.name)
                        .includes(sequence.name);
                });

                // Remove from stepHistory
                this.stepHistory.pop();

                // If item was set by previous step, restore original value
                Object.keys(getStep.formData)?.forEach((e) => {
                    let restore = this.stepHistory
                        .flatMap((history) => history.formData)
                        .find((restoreStep) => {
                            return restoreStep[e];
                        });
                    if (restore) return (this.form[e] = restore[e]);
                    // Otherwise clear values
                    this.form[e] = null;
                });

                // If item was set by previous step, restore original value
                Object.keys(getStep.componentData)?.forEach((e) => {
                    let restore = this.stepHistory
                        .flatMap((history) => history.componentData)
                        .find((restoreStep) => {
                            return restoreStep[e];
                        });
                    if (restore) return (this.$data[e] = restore[e]);
                    // Otherwise clear values
                    this.$data[e] = null;
                });
            });
        },
    },

    computed: {
        currentStep() {
            return this.selectableOptions.find(
                (e) => e.step == this.stepNumber
            );
        },
        previousStep() {
            return this.selectableOptions.find(
                (e) => e.step == this.stepNumber - 1
            );
        },
    },
};
